import { API, graphqlOperation } from "aws-amplify";
import { updateRequests } from "../graphql/mutations";
import { emailsByAdminErrorLogId } from "../graphql/admin-error-log-queries";
import moment from 'moment';

import {
  dateTimeStr
} from "./format";
import {
  isPaymentsUatLocalEnabled,
  paymentsUatLocalPath,
  fetchPaymentsLocalDirectoryToken,
} from "./payments-local";

const sandbox_promotionId = "2345f0aa-db7a-42b9-807b-9e8a82b81ae3";
const prod_promotionId =  "26d09475-32ec-448c-bb43-9f70aea55644";
const host = window.location.host;

function normalizeEmailForLookup(email) {
  let normalized = String(email ?? "").trim();
  if (!normalized) return "";
  try {
    if (normalized.includes("%")) {
      normalized = decodeURIComponent(normalized);
    }
  } catch (err) {
    console.log("Could not decode email for lookup", err);
  }
  return normalized.trim();
}

/** Pull a human-readable message from Amplify API / upstream validation errors. */
export function extractApiErrorMessage(err, fallback = "Request failed.") {
  let data = err?.response?.data;
  if (data == null && typeof err?.response?.body === "string") {
    try {
      data = JSON.parse(err.response.body);
    } catch {
      data = err.response.body;
    }
  } else if (data == null && err?.response?.body != null) {
    data = err.response.body;
  }

  const payload = data?.error ?? data;
  if (Array.isArray(payload)) {
    const descriptions = payload
      .map((item) => item?.description || item?.message)
      .filter(Boolean);
    if (descriptions.length) return descriptions.join(". ");
  }
  if (typeof payload === "string" && payload.trim()) return payload;
  if (payload?.description) return payload.description;
  if (typeof data?.message === "string" && data.message.trim()) return data.message;
  if (
    typeof err?.message === "string" &&
    err.message.trim() &&
    !/^Request failed with status code \d+$/i.test(err.message)
  ) {
    return err.message;
  }
  return fallback;
}

/** First upstream validation / error code when present (e.g. USER_ALREADY_EXISTS). */
export function extractApiErrorCode(err) {
  let data = err?.response?.data;
  if (data == null && typeof err?.response?.body === "string") {
    try {
      data = JSON.parse(err.response.body);
    } catch {
      data = null;
    }
  } else if (data == null && err?.response?.body != null) {
    data = err.response.body;
  }
  const payload = data?.error ?? data;
  if (Array.isArray(payload) && payload[0]?.code) return payload[0].code;
  if (payload?.code) return payload.code;
  return "";
}

export function isUserAlreadyExistsError(err) {
  const code =
    err?.code ||
    extractApiErrorCode(err) ||
    extractApiErrorCode(err?.cause);
  if (String(code).toUpperCase() === "USER_ALREADY_EXISTS") return true;
  const message = String(err?.message || "").toLowerCase();
  return message.includes("user already exists");
}

export const refreshToken = async (clientId, token) => {
  try {
    if (!token || !token.expiresAt) {
      return false;
    }

    const expiresAt = moment(token.expiresAt);
    const now = moment();

    // If token expires in less than 5 minutes, refresh it
    if (expiresAt.diff(now, 'minutes') < 5) {
      console.log("Token expired or expiring soon, refreshing...");
      const payload = JSON.stringify({
        refreshToken: token.refreshToken
      });
      const myInit1 = {
        body: { data: payload || {} },
        queryStringParameters: {
        apiId: `${clientId}ApiId`,
        apiKey: `${clientId}ApiKey`,
        clientId: clientId
      },
      };
      return await API.post("developerApi", "/refreshToken", myInit1);
    } else {
      console.log("Token still valid, expires in", expiresAt.diff(now, 'minutes'), "minutes");
      return token;
    }
  } catch (err) {
    console.log("error checking token is valid", err);
    return false;
  }
}

export const getToken = async (clientId, organizationId) => {
  try {
    const payload = JSON.stringify({
      organizationId: organizationId
    });
    const myInit1 = {
      body: { data: payload || {} },
      queryStringParameters: {
        apiId: `${clientId}ApiId`,
        apiKey: `${clientId}ApiKey`,
        clientId: clientId
      },
    };
    return await API.post("developerApi", "/token", myInit1)
  } catch (err) {
    console.log("error getting token", err);
    return null;
  }
}

/** Retry transient token failures (502 / empty body) before treating as connected-org error. */
export async function getTokenWithRetry(
  clientId,
  organizationId,
  retries = 2,
  delayMs = 500
) {
  let attempt = 0;
  while (attempt <= retries) {
    const result = await getToken(clientId, organizationId);
    if (result?.token) return result;
    if (attempt < retries) await sleep(delayMs);
    attempt += 1;
  }
  return null;
}

export const getOffersByToken = async (token) => {
  try {
    const promo = getPromotionId();
    const myInit1 = {
        queryStringParameters: {
          token,
          promotionId: promo,
        },
      };
      return await API.get("developerApi", "/offers2", myInit1)
  } catch (err) {
    console.log("error getting offers by token", err);
    return null;
  }
}

export const getRoles = async (clientId, token) => {
  try {
    const myInit1 = {
      queryStringParameters: {
        apiId: `${clientId}ApiId`,
        apiKey: `${clientId}ApiKey`,
        clientId: clientId,
        token: token
      },
    };
    return await API.get("developerApi", "/roles-channels", myInit1)
  } catch (err) {
    console.log("error getting roles", err);
    return null;
  }
}
/** Parse SharedApp `offers` AWSJSON into offer id strings. */
export function parseSharedAppOfferIds(offers) {
  if (offers == null || offers === "") return [];
  let parsed = offers;
  if (typeof offers === "string") {
    try {
      parsed = JSON.parse(offers);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(parsed)) return [];
  return parsed
    .map((o) => {
      if (o == null) return "";
      if (typeof o === "string" || typeof o === "number") return String(o).trim();
      if (typeof o === "object") {
        const oid = o.id ?? o.offerId ?? o.value ?? o.uuid;
        if (oid != null && typeof oid !== "object") return String(oid).trim();
      }
      return "";
    })
    .filter(Boolean);
}

/** Primary id on an offer row from GET /offers (payloads may use `id` or `offerId`). */
export function getOfferRowId(o) {
  if (!o || typeof o !== "object") return null;
  const rid = o.id ?? o.offerId;
  return rid != null && rid !== "" ? String(rid).trim() : null;
}

/** Whether a submitted offer id is allowed (channel catalog, or configured ids when channel is empty). */
export function isSubmitOfferAllowed(
  offerId,
  { channelOfferIds = [], configuredOfferIds = [] } = {}
) {
  const id = String(offerId ?? "").trim();
  if (!id) return false;

  const channelIds = (channelOfferIds ?? [])
    .map((cid) => String(cid).trim())
    .filter(Boolean);
  if (channelIds.length > 0) {
    return channelIds.includes(id);
  }

  const configuredIds = (configuredOfferIds ?? [])
    .map((cid) => String(cid).trim())
    .filter(Boolean);
  return configuredIds.includes(id);
}

export const submitOfferInvalidMessage =
  "Selected offer is not available for this application";

/** Restrict API offer rows to SharedApp-configured ids. */
export function filterOffersByConfiguredIds(offersArray, configuredIds) {
  if (!Array.isArray(offersArray) || !configuredIds?.length) return [];
  const want = new Set(configuredIds.map((id) => String(id).trim()));
  return offersArray.filter((o) => {
    const rid = getOfferRowId(o);
    return rid != null && want.has(rid);
  });
}

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/** Auth / permission failures will not succeed on retry with the same keys. */
export function isPermanentOffersHttpStatus(status) {
  const code = Number(status);
  return code === 401 || code === 403;
}

export function sharedAppOffersFetchFailedErrorMessage({ status } = {}) {
  const code = Number(status);
  if (isPermanentOffersHttpStatus(code)) {
    return `Error getting offers: API authentication failed (${code}). Check partner API keys.`;
  }
  if (Number.isFinite(code) && code > 0) {
    return `Error getting offers, offer fetch failed (${code})`;
  }
  return "Error getting offers, offer fetch failed";
}

function isOffersFetchSuccess(res) {
  return res != null && !res.failed && Array.isArray(res.data);
}

async function getOffersForSharedAppWithRetry(params, retries = 4) {
  let attempt = 0;
  let lastFailure = null;
  while (attempt <= retries) {
    const res = await getOffersForSharedApp(params);
    if (isOffersFetchSuccess(res)) return res;
    if (res?.failed) {
      lastFailure = res;
      // Bad API keys / forbidden � do not retry-storm.
      if (res.permanent) return res;
    } else if (res == null) {
      lastFailure = { failed: true, status: null, permanent: false, data: null };
    }
    if (attempt < retries) await sleep(800);
    attempt += 1;
  }
  return (
    lastFailure || { failed: true, status: null, permanent: false, data: null }
  );
}

/**
 * Load offer catalog: channel first, then allOffers filtered by SharedApp offer ids.
 * Distinguishes API failures (null) from a genuinely empty catalog.
 */
export async function resolveSharedAppOfferCatalog({
  clientId,
  channel,
  offers: sharedAppOffers,
  useConnectedOrgs = false,
  parentOrganizationClientId = "",
  organizationId = "",
  token = null,
  sharedAppId = "",
} = {}) {
  const sharedAppOfferParams = {
    useConnectedOrgs,
    parentOrganizationClientId,
    organizationId,
    token,
  };
  const configuredIds = parseSharedAppOfferIds(sharedAppOffers);
  const ch = typeof channel === "string" ? channel.trim() : "";

  let catalogRows = [];
  let channelOfferRows = [];
  let channelFetchFailed = false;
  let usedAlternativeOffers = false;
  let lastFetchStatus = null;
  let permanentFetchFailure = false;

  if (ch) {
    const chRes = await getOffersForSharedAppWithRetry({
      clientId,
      channel: ch,
      sharedAppId,
      ...sharedAppOfferParams,
    });
    if (!isOffersFetchSuccess(chRes)) {
      channelFetchFailed = true;
      lastFetchStatus = chRes?.status ?? null;
      permanentFetchFailure = Boolean(chRes?.permanent);
      // Same partner keys � allOffers / fallbacks will also 401/403.
      if (permanentFetchFailure) {
        return {
          outcome: "fetch_failed",
          data: [],
          configuredIds,
          channelOfferIds: [],
          status: lastFetchStatus,
          permanent: true,
        };
      }
    } else {
      channelOfferRows = chRes.data ?? [];
      catalogRows = channelOfferRows;
    }
  }

  const channelOfferIds = channelOfferRows
    .map(getOfferRowId)
    .filter(Boolean);

  if (catalogRows.length === 0 && configuredIds.length > 0) {
    const allRes = await getOffersForSharedAppWithRetry({
      clientId,
      allOffers: true,
      sharedAppId,
      ...sharedAppOfferParams,
    });
    if (!isOffersFetchSuccess(allRes)) {
      return {
        outcome: "fetch_failed",
        data: [],
        configuredIds,
        channelOfferIds,
        status: allRes?.status ?? lastFetchStatus,
        permanent: Boolean(allRes?.permanent),
      };
    }
    catalogRows = filterOffersByConfiguredIds(allRes.data ?? [], configuredIds);
  }

  if (catalogRows.length === 0 && channelFetchFailed && ch) {
    const allRes = await getOffersForSharedAppWithRetry({
      clientId,
      allOffers: true,
      sharedAppId,
      ...sharedAppOfferParams,
    });
    if (isOffersFetchSuccess(allRes)) {
      const fallbackRows =
        configuredIds.length > 0
          ? filterOffersByConfiguredIds(allRes.data ?? [], configuredIds)
          : allRes.data ?? [];
      if (fallbackRows.length > 0) {
        catalogRows = fallbackRows;
        // Only log offer replacement when channel failed and we had no configured ids.
        if (configuredIds.length === 0) {
          usedAlternativeOffers = true;
        }
      }
    } else {
      lastFetchStatus = allRes?.status ?? lastFetchStatus;
      permanentFetchFailure = Boolean(allRes?.permanent);
    }
  }

  if (catalogRows.length === 0) {
    if (channelFetchFailed) {
      return {
        outcome: "fetch_failed",
        data: [],
        configuredIds,
        channelOfferIds,
        status: lastFetchStatus,
        permanent: permanentFetchFailure,
      };
    }
    return { outcome: "empty", data: [], configuredIds, channelOfferIds };
  }

  if (usedAlternativeOffers) {
    return {
      outcome: "offer_replacement",
      data: catalogRows,
      configuredIds,
      channelOfferIds,
    };
  }

  return { outcome: "ok", data: catalogRows, configuredIds, channelOfferIds };
}

/**
 * Extra client-side retries when catalog resolution returns fetch_failed.
 * Reduces false-positive admin alerts from transient API failures and effect races.
 */
export async function resolveSharedAppOfferCatalogResilient(
  params,
  { maxExtraAttempts = 3, delayMs = 1500, shouldAbort = () => false } = {}
) {
  let result = await resolveSharedAppOfferCatalog(params);
  let attempt = 0;
  // Do not extra-retry permanent auth failures (401/403) � same keys will keep failing.
  while (
    result.outcome === "fetch_failed" &&
    !result.permanent &&
    attempt < maxExtraAttempts
  ) {
    if (shouldAbort()) return result;
    await sleep(delayMs);
    if (shouldAbort()) return result;
    result = await resolveSharedAppOfferCatalog(params);
    attempt += 1;
  }
  return result;
}

export const SHARED_APP_OFFER_REPLACEMENT_MESSAGE =
  "Offer channel failed to load; alternative offers used";

const OFFER_REPLACEMENT_DEDUPE_MS = 24 * 60 * 60 * 1000;
const OFFER_REPLACEMENT_DEDUPE_KEY_PREFIX = "cs-shared-app:offer-replacement:";

function shouldSkipOfferReplacementNotify(sharedAppId) {
  try {
    const raw = localStorage.getItem(
      `${OFFER_REPLACEMENT_DEDUPE_KEY_PREFIX}${sharedAppId}`
    );
    if (!raw) return false;
    const ts = Number(raw);
    if (!Number.isFinite(ts)) return false;
    return Date.now() - ts < OFFER_REPLACEMENT_DEDUPE_MS;
  } catch {
    return false;
  }
}

function markOfferReplacementNotified(sharedAppId) {
  try {
    localStorage.setItem(
      `${OFFER_REPLACEMENT_DEDUPE_KEY_PREFIX}${sharedAppId}`,
      String(Date.now())
    );
  } catch {
    /* ignore quota / private mode */
  }
}

export async function notifyOfferReplacementIfNeeded(
  app,
  sharedAppId,
  catalogResult,
  notifyRef
) {
  if (catalogResult?.outcome !== "offer_replacement") return;
  const id = String(sharedAppId ?? "").trim();
  if (!id) return;
  const notifyKey = `${id}|${SHARED_APP_OFFER_REPLACEMENT_MESSAGE}`;
  if (notifyRef?.current === notifyKey) return;
  if (shouldSkipOfferReplacementNotify(id)) {
    if (notifyRef) notifyRef.current = notifyKey;
    return;
  }
  if (notifyRef) notifyRef.current = notifyKey;
  markOfferReplacementNotified(id);
  console.log("Offer channel failed; alternative offers used", { sharedAppId: id });
  await notifyAdminErrorMessage(
    app,
    id,
    SHARED_APP_OFFER_REPLACEMENT_MESSAGE,
    false,
    { blocking: true }
  );
}

export function sharedAppOffersEmptyErrorMessage(configuredIds = []) {
  return configuredIds.length > 0
    ? "Error getting offers: no offers for this channel, and configured offer ids did not match any promotion offer."
    : "Error getting offers: no offers for this channel, and no configured offers to fall back to.";
}

/** Dedupe getOffers when SharedApp/token effect re-runs with the same inputs. */
export function buildGetOffersEffectKey(sharedApp, elevatedToken) {
  if (!sharedApp?.id) return "";
  const tokenPart =
    elevatedToken?.token != null && String(elevatedToken.token) !== ""
      ? String(elevatedToken.token)
      : "no-token";
  return [
    sharedApp.id,
    sharedApp.clientId ?? "",
    (sharedApp.channel ?? "").trim(),
    tokenPart,
  ].join("|");
}

export function shouldRunGetOffersEffect(effectKeyRef, nextKey) {
  if (!nextKey) return false;
  if (effectKeyRef.current === nextKey) return false;
  effectKeyRef.current = nextKey;
  return true;
}

export function resetGetOffersEffectKey(effectKeyRef) {
  if (effectKeyRef) effectKeyRef.current = "";
}

/**
 * GET /offers for a Shared App (channel catalog or all-by-promotion). Matches partners/edit behavior:
 * uses parent org API keys + optional token when useConnectedOrgs is true.
 *
 * @param {Object} params
 * @param {string} params.clientId - GoodLink client id (query param)
 * @param {string} [params.channel] - omitted when allOffers is true (backend lists all offers for promotion)
 * @param {boolean} [params.allOffers] - if true, do not send channel (full promotion catalog)
 * @param {boolean} [params.useConnectedOrgs]
 * @param {string} [params.parentOrganizationClientId]
 * @param {string} [params.organizationId] � if connected orgs and token omitted, fetches via getToken
 * @param {string|null} [params.token] � elevated access token string
 * @returns {Promise<{ data: unknown[] }|null>}
 */
/** Lambda often forwards upstream JSON as a bare array; some paths use { data: [] }. */
export function normalizeOffersHttpBody(body) {
  if (body == null) return [];
  if (Array.isArray(body)) return body;
  if (Array.isArray(body.data)) return body.data;
  return [];
}

export const getOffersForSharedApp = async ({
  clientId,
  channel,
  allOffers = false,
  useConnectedOrgs = false,
  parentOrganizationClientId = "",
  organizationId = "",
  token = null,
  sharedAppId = "",
  throwOnError = false,
} = {}) => {
  try {
    if (!clientId) {
      return null;
    }
    if (!allOffers && !channel) {
      return null;
    }
    let resolvedToken = token;
    if (
      useConnectedOrgs &&
      parentOrganizationClientId &&
      organizationId &&
      (resolvedToken === null || resolvedToken === undefined)
    ) {
      const t = await getToken(parentOrganizationClientId, organizationId);
      resolvedToken = t?.token ?? null;
    }
    const keyClientId =
      useConnectedOrgs && parentOrganizationClientId
        ? parentOrganizationClientId
        : clientId;
    const promo = getPromotionId();
    const baseQs = {
      apiId: `${keyClientId}ApiId`,
      apiKey: `${keyClientId}ApiKey`,
      clientId,
      promotionId: promo
    };
    const hasToken =
      resolvedToken != null &&
      resolvedToken !== "" &&
      String(resolvedToken).toLowerCase() !== "null";

    let queryStringParameters;
    if (allOffers) {
      // Promotion-wide catalog: upstream URL must not include channels[] � never pass channel here.
      queryStringParameters = { ...baseQs, allOffers: "true" };
      if (hasToken) {
        queryStringParameters.token = resolvedToken;
      }
    } else {
      queryStringParameters = {
        ...baseQs,
        channel
      };
      if (hasToken) {
        queryStringParameters.token = resolvedToken;
      }
    }
    if (sharedAppId) {
      queryStringParameters.sharedAppId = sharedAppId;
    }

    const myInit0 = { queryStringParameters };
    const raw = await API.get("developerApi", "/offers", myInit0);
    return { data: normalizeOffersHttpBody(raw) };
  } catch (err) {
    const status = Number(err?.response?.status);
    const normalizedStatus = Number.isFinite(status) ? status : null;
    console.log("error getting offers for shared app", {
      status: normalizedStatus,
      message: err?.message,
      data: err?.response?.data,
    });
    if (throwOnError) throw err;
    return {
      data: null,
      failed: true,
      status: normalizedStatus,
      permanent: isPermanentOffersHttpStatus(normalizedStatus),
    };
  }
};

export const getOffersByChannel = async (
  clientId,
  channel,
  elevatedToken = undefined,
  throwOnError = false
) => {
  return getOffersForSharedApp({
    clientId,
    channel,
    useConnectedOrgs: false,
    token: elevatedToken ?? null,
    throwOnError,
  });
};

export const getDisclosures = async (clientId) => {
  try {
    const myInit1 = {
        queryStringParameters: {
          apiId: `${clientId}ApiId`,
          apiKey: `${clientId}ApiKey`,
          clientId: clientId
        },
      };
      return await API.get("developerApi", "/disclosures", myInit1)
  } catch (err) {
    console.log("error getting current disclosures", err);
    return null;
  }
}

export const getLoanById = async (clientId, loanId) => {
  try {
    const myInit1 = {
        queryStringParameters: {
          apiId: `${clientId}ApiId`,
          apiKey: `${clientId}ApiKey`,
          clientId: clientId,
          loanId: loanId
        },
      };
      return await API.get("developerApi", "/loanById", myInit1)
  } catch (err) {
    console.log("error getting loan by id", err);
    return null;
  }
}

export const getLoanByTags = async (clientId, tags) => {
  try {
    const loan = await Promise.all(tags.map(async (tag) => {
      const myInit1 = {
        queryStringParameters: {
          apiId: `${clientId}ApiId`,
          apiKey: `${clientId}ApiKey`,
          clientId: clientId,
          tag: tag
        },
      };
      const loanFound = await API.get("developerApi", "/loanByTag", myInit1)
         return {loan: loanFound, tag: tag};
    }));
    return loan.find((l) => l.loan.data.length === 1);
  } catch (err) {
    console.log("error getting loan by tag", err);
    return {loan: null, tag: ""};
  }
}

export const getLoanByTag = async (clientId, tag) => {
  try {
    const myInit1 = {
        queryStringParameters: {
          apiId: `${clientId}ApiId`,
          apiKey: `${clientId}ApiKey`,
          clientId: clientId,
          tag: tag
        },
      };
      return await API.get("developerApi", "/loanByTag", myInit1)
  } catch (err) {
    console.log("error getting loan by tag", err);
    return false;
  }
}

export const deleteTagByLoanId = async (clientId, loanId, tag) => {
  try {
    const payload = JSON.stringify({
        tags: [
          tag
        ]
    });
    const myInit1 = {
      body: { data: payload || {} },
        queryStringParameters: {
          apiId: `${clientId}ApiId`,
          apiKey: `${clientId}ApiKey`,
          clientId: clientId,
          loanId: loanId
        },
      };
      return await API.post("developerApi", "/deleteTag", myInit1)
  } catch (err) {
    console.log("error deleting tag", err);
    return false;
  }
}

export const addTagByLoanId = async (clientId, loanId, tag) => {
  try {
    const payload = JSON.stringify({
        tags: [
          tag
        ]
    });
    const myInit1 = {
      body: { data: payload || {} },
        queryStringParameters: {
          apiId: `${clientId}ApiId`,
          apiKey: `${clientId}ApiKey`,
          clientId: clientId,
          loanId: loanId
        },
      };
      return await API.post("developerApi", "/addTag", myInit1)
  } catch (err) {
    console.log("error adding tag", err);
    return false;
  }
}

export const applyForLoan = async (clientId, payload) => {
  try {
    const myInit1 = {
      body: { data: payload || {} },
        queryStringParameters: {
          apiId: `${clientId}ApiId`,
          apiKey: `${clientId}ApiKey`,
          clientId: clientId
        },
      };
      return await API.post("developerApi", "/submitLoan", myInit1)
  } catch (err) {
    console.log("error submitting loan application", err);
    return null;
  }
}

export const changeOrders = async (clientId, loanId, payload) => {
  try {
    const myInit1 = {
      body: { data: payload || {} },
        queryStringParameters: {
          apiId: `${clientId}ApiId`,
          apiKey: `${clientId}ApiKey`,
          clientId: clientId,
          loanId: loanId
        },
      };
      return await API.post("developerApi", "/changeOrders", myInit1)
  } catch (err) {
    console.log("error submitting change order", err);
    return false;
  }
}

export const requestLoanDocuments = async (clientId, loanId) => {
  try {
    const myInit1 = {
        queryStringParameters: {
          apiId: `${clientId}ApiId`,
          apiKey: `${clientId}ApiKey`,
          clientId: clientId,
          loanId: loanId
        },
      };
      return await API.post("developerApi", "/requestLoanDocuments", myInit1)
  } catch (err) {
    console.log("error requesting loan documents", err);
    return false;
  }
}

export const getCategories = async (clientId, token) => {
  try {
    const myInit1 = {
        queryStringParameters: {
          apiId: `${clientId}ApiId`,
          apiKey: `${clientId}ApiKey`,
          clientId: clientId,
          token
        },
      };
      return await API.get("developerApi", "/categories", myInit1)
  } catch (err) {
    console.log("error getting categories", err);
    return false;
  }
}

export const getLimitations = async (clientId, elevatedToken = undefined) => {
  try {
    const myInit1 = {
        queryStringParameters: {
          apiId: `${clientId}ApiId`,
          apiKey: `${clientId}ApiKey`,
          clientId: clientId,
          token: elevatedToken
        },
      };
      return await API.get("developerApi", "/limitations", myInit1)
  } catch (err) {
    console.log("error getting limitations", err);
    return false;
  }
}

const getPromotionId = () => {
  // if (host.includes("sandbox") || (host.includes("localhost"))) {
  if (host.includes("sandbox")) {
    return sandbox_promotionId;
  } else  {
    return prod_promotionId;
  }
}

export const getPayment = async (clientId, offerId, loanAmount, enrollments, elevatedToken = undefined) => {
    try {
    const promo = getPromotionId();
    const autopay = enrollments && enrollments.length !== 0 && enrollments.includes("AUTOPAY");
    let myInit1 = {
        queryStringParameters: {
          apiId: `${clientId}ApiId`,
          apiKey: `${clientId}ApiKey`,
          clientId: clientId,
          amount: loanAmount,
          offerId: offerId,
          token: elevatedToken
        },
      };
      if (autopay) myInit1.queryStringParameters = {...myInit1.queryStringParameters, promotionId: promo };
      console.log(autopay, enrollments, myInit1);
      return await API.get("developerApi", "/payments", myInit1)
  } catch (err) {
    console.log("error getting payments", err);
    return false;
  }
}

export const getOfferByLoanId = async (clientId, loanId) => {
  try {
    const myInit1 = {
        queryStringParameters: {
          apiId: `${clientId}ApiId`,
          apiKey: `${clientId}ApiKey`,
          clientId: clientId,
          loanId: loanId
        },
      };
      return await API.get("developerApi", "/offersByLoanId", myInit1)
  } catch (err) {
    console.log("error getting offer by loan id", err);
    return false;
  }
}

export const getLoanStatus = async (clientId, loanId) => {
  try {
    const myInit1 = {
        queryStringParameters: {
          apiId: `${clientId}ApiId`,
          apiKey: `${clientId}ApiKey`,
          clientId: clientId,
          loanId: loanId
        },
      };
      return await API.get("developerApi", "/loanStatus", myInit1)
  } catch (err) {
    console.log("error getting loan status", err);
    return false;
  }
}

export const getUsers = async (clientId, elevatedToken = undefined) => {
  try {
    const myInit1 = {
      queryStringParameters: {
        apiId: `${clientId}ApiId`,
        apiKey: `${clientId}ApiKey`,
        clientId: clientId,
        token: elevatedToken
      },
    };
    return await API.get("developerApi", "/users", myInit1)
  } catch (err) {
    console.log("error getting users", err);
    return false;
  }
}

export const getUserVerificationUrl = async (clientId, userId, elevatedToken = undefined) => {
  try {
    const myInit1 = {
      queryStringParameters: {
        apiId: `${clientId}ApiId`,
        apiKey: `${clientId}ApiKey`,
        clientId: clientId,
        userId: userId,
        token: elevatedToken
      },
    };
    return await API.get("developerApi", "/users/verification-url", myInit1)
  } catch (err) {
    console.log("error getting user verification url", err);
    return false;
  }
}

export const getUserById = async (clientId, userId, elevatedToken = undefined) => {
  try {
    // get userById
    const myInit1 = {
        queryStringParameters: {
          apiId: `${clientId}ApiId`,
          apiKey: `${clientId}ApiKey`,
          clientId: clientId,
          userId: userId,
          token: elevatedToken
        },
      };
    return await API.get("developerApi", "/users", myInit1)
  } catch (err) {
    console.log("error getting user by id", err);
    return false;
  }
}

export const addChannelsToUser = async (clientId, states, sUserId) => {
  let channelsMap = [];
  channelsMap = states.map((st) => {
    let stateMap = [];
    stateMap = st.channels.map((ch) => {
      return  [
        ...stateMap,
        `${st.code}_${ch}`
      ]
    })
    return [
      ...channelsMap,
      ...stateMap.flat()
    ]
  });
  const allChannels = channelsMap.flat();
  try {
        const payload = JSON.stringify({
          channels: allChannels
        });
        const myInit = {
          body: { data: payload || {} },
          queryStringParameters: {
            apiId: `${clientId}ApiId`,
          apiKey: `${clientId}ApiKey`,
          clientId: clientId,
            userId: sUserId
          },
        };
        return await API.post("developerApi", "/user-channels", myInit);
    } catch (err) {
        console.log("error updating channels", err);
    }
}

const trimAndRemoveSpaces = (str) => {
  // Trim leading and trailing spaces
  str = str.trim();

  // Remove spaces within the string
  str = str.replace(/\s+/g, '');

  return str;
}

export const addLicenseToUser = async (clientId, sUserId, license, state) => {
  try {
        const payload = JSON.stringify({
          id: trimAndRemoveSpaces(license),
          appliesTo: state
        });
        const myInit = {
           body: { data: payload || {} },
           queryStringParameters: {
           apiId: `${clientId}ApiId`,
           apiKey: `${clientId}ApiKey`,
           clientId: clientId,
           userId: sUserId
          },
        };
        return await API.post("developerApi", "/user-license", myInit);
    } catch (err) {
      console.log("error updating user license", err);
       return("Error");
    }
}

export const getStates = async (clientId) => {
    try {
      const myInit0 = {
        queryStringParameters: {
          apiId: `${clientId}ApiId`,
          apiKey: `${clientId}ApiKey`,
          clientId: clientId
        },
      };

      // get states
     return await API.get("developerApi", "/states", myInit0)
    } catch (err) {
      console.log("error getting states", err);
      return false;
    }
  }

export const getChannels = async (clientId, state, elevatedToken = undefined) => {
    try {
      const myInit0 = {
        queryStringParameters: {
          apiId: `${clientId}ApiId`,
          apiKey: `${clientId}ApiKey`,
          clientId: clientId,
          state: state || '',
          token: elevatedToken
        },
      };
      // get states
     return await API.get("developerApi", "/channelsByState", myInit0)
    } catch (err) {
      console.log("error getting channels", err);
      return false;
    }
  }

export const getOffersByCategory = async (
  clientId,
  categoryId,
  state,
  env,
  elevatedToken = undefined,
  channel = undefined
) => {
    try {
      const promo = getPromotionId();
      const queryStringParameters = {
        apiId: `${clientId}ApiId`,
        apiKey: `${clientId}ApiKey`,
        clientId: clientId,
        categoryId: categoryId,
        state: state || '',
        env: env,
        promotionId: promo,
        token: elevatedToken
      };
      const ch = channel != null ? String(channel).trim() : "";
      if (ch !== "") {
        queryStringParameters.channel = ch;
      }
      const myInit0 = {
        queryStringParameters,
      };
      // get states
     return await API.get("developerApi", "/offersByCategory", myInit0)
    } catch (err) {
      console.log("error getting offers", err);
      return false;
    }
  }

/** User-facing + admin/Slack message when connected-org elevated token fails. */
export function connectedOrgTokenErrorMessage(
  parentOrganizationClientId,
  organizationId
) {
  const parent = String(parentOrganizationClientId || "").trim();
  const orgId = String(organizationId || "").trim();
  let msg =
    "Connected org connection could not be made: unable to retrieve elevated access token.";
  const details = [
    parent ? `parent org: ${parent}` : "",
    orgId ? `organization: ${orgId}` : "",
  ].filter(Boolean);
  if (details.length) {
    msg += ` (${details.join(", ")})`;
  }
  return msg;
}

/** Slack/admin message when configured submitting user is replaced with an admin fallback. */
export function submittingUserSubstitutedMessage(
  configuredEmail,
  substituteEmail,
  roleName
) {
  const configured = String(configuredEmail || "").trim();
  const substitute = String(substituteEmail || "").trim();
  const role = String(roleName || "").trim();
  let msg = `Configured submitting user was not found; using substitute user "${substitute}".`;
  if (configured) {
    msg = `Configured submitting user "${configured}" was not found; using substitute "${substitute}".`;
  }
  if (role) {
    msg += ` (${role})`;
  }
  return msg;
}

/**
 * Non-fatal / empty-catalog config issues � show in UI but do not write admin
 * error log or page Slack. Exhausted offer API failures (401 keys, 5xx, etc.)
 * are not suppressed.
 */
const SLACK_SUPPRESSED_ERROR_PATTERNS = [
  /error getting limitations/i,
  /error getting categories/i,
  /error getting payments/i,
  /no offers for this channel/i,
  /configured offer ids did not match/i,
  /no configured offers to fall back/i,
  /error getting offers:\s*no offers/i,
  /error getting offers, no offers returned/i,
  // Exhausted offer fetch failures (incl. 401 API keys) must reach Error Log / Slack.
  /captcha verification failed/i,
  /captcha expired/i,
];

const slackNotifyDedupeKeys = new Set();
const slackNotifyInFlight = new Set();

/** Named Slack routes � SSM under KEYS prefix (see slack-notify.js) */
export const SLACK_CHANNEL_ROUTES = {
  QUICKLINKS_ALERTS: "quicklinks-alerts",
  GOODLEAPPLUS_ALERTS: "goodleapplus-alerts",
};

function resolveSlackChannelRouteForApp(app, explicitRoute) {
  if (explicitRoute) return explicitRoute;
  if (app?.parentOrganizationClientId === "SRSIntegrator") {
    return SLACK_CHANNEL_ROUTES.GOODLEAPPLUS_ALERTS;
  }
  return SLACK_CHANNEL_ROUTES.QUICKLINKS_ALERTS;
}

/** True only when caller requested Slack and the message is a real operational error. */
export function shouldNotifyAdminSlack(errorMessage, notifySlackRequested = false) {
  if (!notifySlackRequested) return false;
  const msg = String(errorMessage ?? "").trim();
  if (!msg) return false;
  return !SLACK_SUPPRESSED_ERROR_PATTERNS.some((re) => re.test(msg));
}

function isAdminNotifySuppressed(errorMessage) {
  const msg = String(errorMessage ?? "").trim();
  if (!msg) return true;
  return SLACK_SUPPRESSED_ERROR_PATTERNS.some((re) => re.test(msg));
}

/** True when admin should receive email + error log. */
export function shouldNotifyAdminError(
  errorMessage,
  { notifySlack = false, blocking = false } = {}
) {
  if (isAdminNotifySuppressed(errorMessage)) return false;
  if (blocking) return true;
  return shouldNotifyAdminSlack(errorMessage, notifySlack);
}

export function hasUpstreamListPayload(response) {
  return (
    response != null &&
    response !== false &&
    Array.isArray(response.data)
  );
}

export const notifyAdminErrorMessage = async (
  app,
  id,
  error,
  notifySlack = false,
  { slackChannelRoute, blocking = false, initiative } = {}
) => {
  let dedupeKey = "";
  try {
    const msg = String(error ?? "").trim();
    if (!msg) return null;

    const notifyAdmin = shouldNotifyAdminError(msg, { notifySlack, blocking });
    if (!notifyAdmin) return null;

    const notifySlackChannel = shouldNotifyAdminSlack(msg, notifySlack);
    const route = resolveSlackChannelRouteForApp(app, slackChannelRoute);
    dedupeKey = `${id ?? ""}|${msg}|${route}|${blocking ? "blocking" : "ops"}`;
    if (slackNotifyDedupeKeys.has(dedupeKey) || slackNotifyInFlight.has(dedupeKey)) {
      return null;
    }
    slackNotifyInFlight.add(dedupeKey);
    slackNotifyDedupeKeys.add(dedupeKey);
    console.log("Notifying admin / Slack", app?.clientId, id, route, {
      blocking,
      notifySlackChannel,
      initiative,
    });
    const myParams = {
      queryStringParameters: {
        formUrl: app ? app.formUrl : "",
        clientId: app ? app.clientId : "",
        parentOrganizationClientId: app?.parentOrganizationClientId ?? "",
        organizationId: app?.organizationId ?? "",
        partnerName: app?.partnerName ?? "",
        accountName: app?.accountName ?? app?.partnerName ?? "",
        id: id,
        error: msg,
        notifySlack: notifySlackChannel ? "true" : "false",
        slackChannelRoute: route,
        initiative: initiative ? String(initiative).trim() : "",
      },
    };
    await API.post("developerApi", "/notifyAdminErrorMessage", myParams);
    console.log("Admin / Slack notification request sent");
  } catch (err) {
    console.log("Error Notifying Admin:", err);
  } finally {
    if (dedupeKey) {
      slackNotifyInFlight.delete(dedupeKey);
    }
  }
}

export async function sendAdminErrorEmail(adminErrorLogId, toEmails = []) {
  const id = String(adminErrorLogId ?? "").trim();
  const recipients = (Array.isArray(toEmails) ? toEmails : [])
    .map((email) => String(email ?? "").trim())
    .filter(Boolean);
  if (!id || !recipients.length) {
    throw new Error("adminErrorLogId and at least one recipient are required");
  }
  return API.post("developerApi", "/sendAdminErrorEmail", {
    queryStringParameters: {
      adminErrorLogId: id,
      toEmails: recipients.join(","),
    },
  });
}

export async function listAdminErrorEmailSends(adminErrorLogId, { limit = 50 } = {}) {
  const id = String(adminErrorLogId ?? "").trim();
  if (!id) return [];
  const data = await API.graphql(
    graphqlOperation(emailsByAdminErrorLogId, {
      adminErrorLogId: id,
      sortDirection: "DESC",
      limit,
    })
  );
  return data?.data?.emailsByAdminErrorLogId?.items ?? [];
}

export async function getAdminErrorEmailConfig() {
  return API.get("developerApi", "/adminErrorEmailConfig", {});
}

export async function saveAdminErrorEmailConfig({
  autoEmailTemplateIds = [],
  testRecipient = "",
  templateTestRecipients = {},
  updatedBy = "",
} = {}) {
  return API.post("developerApi", "/adminErrorEmailConfig", {
    body: {
      autoEmailTemplateIds: Array.isArray(autoEmailTemplateIds)
        ? autoEmailTemplateIds
        : [],
      testRecipient: String(testRecipient ?? "").trim(),
      templateTestRecipients:
        templateTestRecipients && typeof templateTestRecipients === "object"
          ? templateTestRecipients
          : {},
      updatedBy: String(updatedBy ?? "").trim(),
    },
  });
}

export const updateNotesRequest = async (req, message ) => {
    try {
        let note = req.notes;
        if (note !== null && note.trim() !== "") note = note.concat("\n");
        const _app = { ...req, notes: note.concat(dateTimeStr(Date.now())).concat(" - " + message) };
        await API.graphql(graphqlOperation(updateRequests, { input: _app }));
    } catch (err) {
        console.log("Error updating Request", err);
    }
  }

  export const createUser = async (clientId, payload, elevatedToken = undefined) => {
    try {
      const myInit1 = {
        body: { data: payload || {} },
        queryStringParameters: {
          apiId: `${clientId}ApiId`,
          apiKey: `${clientId}ApiKey`,
          clientId: clientId,
          token: elevatedToken
        },
      };
      return await API.post("developerApi", "/create-user", myInit1)
    } catch (err) {
      console.log("error creating user", err);
      const message = extractApiErrorMessage(err, "Error creating user.");
      const wrapped = new Error(message);
      wrapped.cause = err;
      wrapped.code = extractApiErrorCode(err);
      throw wrapped;
    }
  }

  export const inviteUser = async (clientId, payload, elevatedToken = undefined) => {
    try {
      const myInit1 = {
        body: { data: payload || {} },
        queryStringParameters: {
          apiId: `${clientId}ApiId`,
          apiKey: `${clientId}ApiKey`,
          clientId: clientId,
          token: elevatedToken
        },
      };
      return await API.post("developerApi", "/invite-user", myInit1)
    } catch (err) {
      console.log("error inviting user", err);
      const message = extractApiErrorMessage(err, "Error inviting user.");
      const wrapped = new Error(message);
      wrapped.cause = err;
      wrapped.code = extractApiErrorCode(err);
      throw wrapped;
    }
  }

  export const userChannels = async (clientId, userId, payload, elevatedToken = undefined) => {
    try {
      const myInit1 = {
        body: { data: payload || {} },
        queryStringParameters: {
          apiId: `${clientId}ApiId`,
          apiKey: `${clientId}ApiKey`,
          clientId: clientId,
          userId: userId,
          token: elevatedToken
        },
      };
      return await API.post("developerApi", "/user-channels", myInit1)
    } catch (err) {
      console.log("error updating user channels", err);
      return false;
    }
  }

  export const getUserByEmail = async (
    clientId,
    email,
    elevatedToken = undefined,
    throwOnError = false
  ) => {
    try {
      const normalizedEmail = normalizeEmailForLookup(email);
      const myInit1 = {
        queryStringParameters: {
          apiId: `${clientId}ApiId`,
          apiKey: `${clientId}ApiKey`,
          clientId: clientId,
          email: encodeURIComponent(normalizedEmail),
          token: elevatedToken
        },
      };
      return await API.get("developerApi", "/users", myInit1)
    } catch (err) {
      console.log("error getting user by email", err);
      if (throwOnError) throw err;
      return false;
    }
  }

  /**
   * Classify a failed POST /loans-share for admin alerting.
   * 4xx = API rejected the request.
   * 5xx = API responded with a server error (log it; share may or may not exist).
   * No HTTP status (timeout/network) is indeterminate — do not alert (false positives).
   */
  export function classifyLoanShareCreateError(err) {
    const status = Number(err?.response?.status);
    const apiError = err?.response?.data?.error;
    const hasHttpStatus = Number.isFinite(status);
    const confirmedApiRejection =
      hasHttpStatus && status >= 400 && status < 500;
    const serverError = hasHttpStatus && status >= 500;
    return {
      status: hasHttpStatus ? status : null,
      apiError: apiError || null,
      confirmedApiRejection,
      serverError,
      shouldNotifyAdmin: confirmedApiRejection || serverError,
    };
  }

  export function loanShareCreateErrorMessage(
    err,
    { label = "Shared App" } = {}
  ) {
    const { status, apiError, confirmedApiRejection, serverError } =
      classifyLoanShareCreateError(err);
    if (confirmedApiRejection) {
      return `Error creating ${label}: ${
        apiError || `API rejected the request (${status})`
      }`;
    }
    if (serverError) {
      return `Error creating ${label}: ${
        apiError || `API error (${status})`
      }`;
    }
    return `${label} request outcome could not be confirmed. Check GoodLeap before retrying.`;
  }

  export const postLoanShare = async (
    clientId,
    payload,
    elevatedToken = undefined,
    { sharedAppId = "", captchaResponse = "" } = {}
  ) => {
    const token =
      elevatedToken != null &&
      elevatedToken !== "" &&
      String(elevatedToken).toLowerCase() !== "null" &&
      String(elevatedToken).toLowerCase() !== "undefined"
        ? elevatedToken
        : undefined;
    const body = {
      data: payload || {},
      captchaResponse,
      sharedAppId,
    };
    if (token) {
      body.token = token;
    }
    const myInit1 = {
      body,
      queryStringParameters: {
        apiId: `${clientId}ApiId`,
        apiKey: `${clientId}ApiKey`,
        clientId: clientId,
        sharedAppId,
        promotionId: getPromotionId(),
      },
    };
    return await API.post("developerApi", "/loans-share", myInit1);
  }

  // Payments API — proxy via developerApi (same SSM keys + optional elevated token)
  // On localhost + REACT_APP_PAYMENTS_UAT_LOCAL, use /payments-uat → sandbox01 (see setupProxy.js)

function paymentsQueryParams(clientId, elevatedToken, extra = {}) {
  const qs = {
    apiId: `${clientId}ApiId`,
    apiKey: `${clientId}ApiKey`,
    clientId,
    ...extra,
  };
  const token =
    elevatedToken != null &&
    elevatedToken !== "" &&
    String(elevatedToken).toLowerCase() !== "null" &&
    String(elevatedToken).toLowerCase() !== "undefined"
      ? elevatedToken
      : undefined;
  if (token) qs.token = token;
  return qs;
}

async function paymentsUatLocalFetch(method, apiPath, { body } = {}) {
  const headers = {
    Accept: "application/json",
    "Content-Type": "application/json",
  };
  // Auth is applied by src/setupProxy.js from payments-local.json (Basic).
  const res = await fetch(paymentsUatLocalPath(apiPath), {
    method,
    headers,
    body: body != null ? JSON.stringify(body) : undefined,
  });
  const text = await res.text();
  let data = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = text;
  }
  if (!res.ok) {
    const err = new Error(
      (data && (data.error || data.message)) ||
        `Payments UAT request failed (${res.status})`
    );
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return data;
}

function parsePaymentsConfig(data) {
  if (!data || typeof data !== "object") return null;
  if (data.payload && typeof data.payload === "object") {
    return data.payload;
  }
  if (typeof data.paymentsEnabled === "boolean") {
    return {
      paymentsEnabled: data.paymentsEnabled,
      dualPricingPercent: data.dualPricingPercent,
    };
  }
  return null;
}

function parseTransactionResponse(data) {
  if (!data || typeof data !== "object") return null;
  if (data.payload && typeof data.payload === "object") return data.payload;
  if (typeof data.id === "string") return data;
  return null;
}

function parseCustomerResponse(data) {
  if (!data || typeof data !== "object") return null;
  if (data.payload && typeof data.payload === "object") return data.payload;
  if (typeof data.id === "string") return data;
  return null;
}

function findHttpUrl(value, depth = 0) {
  if (depth > 6 || value == null) return undefined;
  if (typeof value === "string") {
    const trimmed = value.trim();
    if (/^https?:\/\//i.test(trimmed)) return trimmed;
    return undefined;
  }
  if (Array.isArray(value)) {
    for (const item of value) {
      const found = findHttpUrl(item, depth + 1);
      if (found) return found;
    }
    return undefined;
  }
  if (typeof value === "object") {
    for (const nested of Object.values(value)) {
      const found = findHttpUrl(nested, depth + 1);
      if (found) return found;
    }
  }
  return undefined;
}

/** Public invoice page host for Payments (sandbox vs production). */
export function getInvoiceHostBaseUrl(apiEnvironment = "sandbox") {
  if (isPaymentsUatLocalEnabled()) {
    return "https://invoice.payments-sandbox.goodleap.com";
  }
  if (apiEnvironment === "production") {
    return "https://invoice.payments.goodleap.com";
  }
  return "https://invoice.payments-sandbox.goodleap.com";
}

export function resolveInvoiceLink(invoice, apiEnvironment = "sandbox") {
  const env = isPaymentsUatLocalEnabled() ? "sandbox" : apiEnvironment;
  if (invoice?.url?.startsWith("http")) return invoice.url;
  return `${getInvoiceHostBaseUrl(env)}/i/${invoice.id}`;
}

function parseInvoiceResponse(data) {
  if (!data || typeof data !== "object") return null;
  const payload =
    data.payload && typeof data.payload === "object"
      ? data.payload
      : typeof data.id === "string"
        ? data
        : null;
  if (!payload?.id) return null;
  const url = findHttpUrl(payload) || (typeof payload.url === "string" ? payload.url : undefined);
  return {
    id: String(payload.id),
    transactionId:
      typeof payload.transactionId === "string" ? payload.transactionId : undefined,
    referenceNumber:
      typeof payload.referenceNumber === "string" ? payload.referenceNumber : undefined,
    url,
  };
}

/**
 * Mint directory Bearer for payment-pos (e-checks, etc.).
 * Localhost UAT: directory-dev via CRA proxy (payments-local.json credentials).
 * Deployed: Lambda → directory.loanpal.services (prod) or directory-dev (non-prod),
 * using SSM partner keys for clientId (organizationId:apiKey).
 */
export const getPaymentsDirectoryToken = async (
  clientId,
  overrides = undefined
) => {
  try {
    if (isPaymentsUatLocalEnabled()) {
      return await fetchPaymentsLocalDirectoryToken();
    }
    const body = {};
    if (overrides?.organizationId) body.organizationId = overrides.organizationId;
    if (overrides?.apiKey) body.apiKey = overrides.apiKey;
    const myInit = {
      body: Object.keys(body).length ? { data: body } : { data: {} },
      queryStringParameters: paymentsQueryParams(clientId),
    };
    return await API.post("developerApi", "/payments-directory-token", myInit);
  } catch (err) {
    console.log("error getting payments directory token", err);
    throw err;
  }
};

export const getPaymentsConfig = async (clientId, elevatedToken = undefined) => {
  try {
    if (isPaymentsUatLocalEnabled()) {
      const response = await paymentsUatLocalFetch("GET", "/payments/v1/config");
      return parsePaymentsConfig(response);
    }
    const myInit = {
      queryStringParameters: paymentsQueryParams(clientId, elevatedToken),
    };
    const response = await API.get("developerApi", "/payments-config", myInit);
    return parsePaymentsConfig(response);
  } catch (err) {
    console.log("error getting payments config", err);
    return null;
  }
};

export const createPaymentCustomer = async (
  clientId,
  payload,
  elevatedToken = undefined
) => {
  try {
    if (isPaymentsUatLocalEnabled()) {
      const response = await paymentsUatLocalFetch(
        "POST",
        "/payments/v1/customers",
        { body: payload || {} }
      );
      return parseCustomerResponse(response);
    }
    const myInit = {
      body: { data: payload || {} },
      queryStringParameters: paymentsQueryParams(clientId, elevatedToken),
    };
    const response = await API.post("developerApi", "/payments-customers", myInit);
    return parseCustomerResponse(response);
  } catch (err) {
    console.log("error creating payment customer", err);
    throw err;
  }
};

export const createPaymentTransaction = async (
  clientId,
  payload,
  elevatedToken = undefined
) => {
  try {
    const amountCents = Math.round(Number(payload?.amount));
    if (!Number.isFinite(amountCents) || amountCents <= 0) {
      throw new Error("Invalid transaction amount");
    }
    const body = { ...(payload || {}), amount: amountCents };
    if (isPaymentsUatLocalEnabled()) {
      const response = await paymentsUatLocalFetch(
        "POST",
        "/payments/v1/transactions",
        { body }
      );
      return parseTransactionResponse(response);
    }
    const myInit = {
      body: { data: body },
      queryStringParameters: paymentsQueryParams(clientId, elevatedToken),
    };
    const response = await API.post("developerApi", "/payments-transactions", myInit);
    return parseTransactionResponse(response);
  } catch (err) {
    console.log("error creating payment transaction", err);
    throw err;
  }
};

export const createPaymentInvoice = async (
  clientId,
  payload,
  elevatedToken = undefined
) => {
  try {
    if (isPaymentsUatLocalEnabled()) {
      const response = await paymentsUatLocalFetch(
        "POST",
        "/payments/v1/invoices",
        { body: payload || {} }
      );
      return parseInvoiceResponse(response);
    }
    const myInit = {
      body: { data: payload || {} },
      queryStringParameters: paymentsQueryParams(clientId, elevatedToken),
    };
    const response = await API.post("developerApi", "/payments-invoices", myInit);
    return parseInvoiceResponse(response);
  } catch (err) {
    console.log("error creating payment invoice", err);
    throw err;
  }
};

/**
 * Send invoice via payment-pos (directory Bearer).
 * Localhost UAT: CRA proxy mints directory-dev token + posts to paymentPosBaseUrl.
 * Deployed: Lambda → payment-pos.payments[-sandbox].goodleap.com/v1/invoices/{id}/send
 *
 * @param {string} clientId
 * @param {{ invoiceId: string, method: "email"|"sms", email?: string, phone?: string }} payload
 */
export const sendPaymentInvoice = async (clientId, payload = {}) => {
  try {
    const invoiceId = String(payload.invoiceId || "").trim();
    const method = String(payload.method || "").trim().toLowerCase();
    const email = String(payload.email || "").trim();
    const phoneDigitsOnly = String(payload.phone || "").replace(/\D/g, "");

    if (!invoiceId) throw new Error("invoiceId is required");
    if (method !== "email" && method !== "sms") {
      throw new Error("method must be email or sms");
    }
    if (method === "email" && !email) {
      throw new Error("email is required");
    }
    if (method === "sms" && phoneDigitsOnly.length !== 10) {
      throw new Error("phone must be 10 digits");
    }

    const body = {
      invoiceId,
      method,
      ...(email ? { email } : {}),
      ...(method === "sms" ? { phone: phoneDigitsOnly } : {}),
    };

    if (isPaymentsUatLocalEnabled()) {
      const res = await fetch("/payments-local-invoice-send", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const text = await res.text();
      let data = null;
      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        data = { raw: text };
      }
      if (!res.ok) {
        const err = new Error(
          (data &&
            (typeof data.error === "string"
              ? data.error
              : data.error?.message || data.message)) ||
            `Invoice send failed (${res.status})`
        );
        err.status = res.status;
        err.data = data;
        throw err;
      }
      return data ?? { success: true };
    }

    const myInit = {
      body: { data: body },
      queryStringParameters: paymentsQueryParams(clientId),
    };
    const response = await API.post(
      "developerApi",
      "/payments-invoice-send",
      myInit
    );
    if (response?.success === false) {
      throw new Error(response?.error || "Failed to send invoice");
    }
    return response;
  } catch (err) {
    console.log("error sending payment invoice", err);
    const message =
      err?.response?.data?.error ||
      err?.data?.error ||
      err?.message ||
      "Failed to send invoice";
    throw new Error(
      typeof message === "string" ? message : "Failed to send invoice"
    );
  }
};

/**
 * Public mint: signed pay link from payLinkId + ref.
 * No API key / Cognito — partners only need their pay link id.
 * Localhost uses CRA proxy + payments-local.json signing key.
 */
export const mintPaymentLink = async ({
  payLinkId,
  sharedAppId, // legacy alias
  ref,
  ttlSeconds,
} = {}) => {
  const id = String(payLinkId || sharedAppId || "").trim();
  const body = {
    payLinkId: id,
    ref,
    ...(ttlSeconds ? { ttlSeconds } : {}),
  };
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    const isLocal =
      host === "localhost" || host === "127.0.0.1" || host === "[::1]";
    if (isLocal) {
      const res = await fetch("/payments-generate-payment-link", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        const detail =
          (typeof data.error === "string" && data.error) ||
          "Failed to mint payment link";
        const err = new Error(detail);
        err.code = data.code;
        err.allowedCharacters = data.allowedCharacters;
        throw err;
      }
      return data;
    }
  }
  const response = await API.post("developerApi", "/payment-link", {
    body: { data: body },
  });
  if (response?.success === false) {
    throw new Error(response?.error || "Failed to mint payment link");
  }
  return response;
};

/** @deprecated Use mintPaymentLink — same public mint. */
export const mintPaymentLinkAdmin = mintPaymentLink;

/** Verify signed ?ref=&exp=&sig= for a pay link id. */
export const verifyPaymentLink = async ({
  payLinkId,
  sharedAppId, // legacy alias
  ref,
  exp,
  sig,
} = {}) => {
  const id = String(payLinkId || sharedAppId || "").trim();
  if (typeof window !== "undefined") {
    const host = window.location.hostname;
    const isLocal =
      host === "localhost" || host === "127.0.0.1" || host === "[::1]";
    if (isLocal) {
      const qs = new URLSearchParams({
        payLinkId: id,
        ref: String(ref || ""),
        exp: String(exp || ""),
        sig: String(sig || ""),
      });
      const res = await fetch(`/payments-generate-payment-link-verify?${qs}`);
      return res.json();
    }
  }
  return API.get("developerApi", "/payment-link-verify", {
    queryStringParameters: {
      payLinkId: id,
      ref,
      exp,
      sig,
    },
  });
};

/**
 * Send consumer invoice email via SES (createShareAppLink).
 * From = submitting user (actor) email.
 */
export const sendPaymentInvoiceEmail = async (payload = {}) => {
  try {
    const response = await API.post("developerApi", "/sendPaymentInvoiceEmail", {
      body: { data: payload },
    });
    if (response?.success === false) {
      throw new Error(response?.error || "Failed to send invoice email");
    }
    return response;
  } catch (err) {
    console.log("error sending payment invoice email", err);
    const message =
      err?.response?.data?.error ||
      err?.message ||
      "Failed to send invoice email";
    throw new Error(
      typeof message === "string" ? message : "Failed to send invoice email"
    );
  }
};
